import { useState } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowLeft, MapPin } from "lucide-react";

// Service-area map, two views.
//
// Kodiak works statewide, but 39 of the service areas sit inside the Twin
// Cities metro, which is a small fraction of Minnesota. Drawn on one map those
// 39 pins collapse into an unreadable blob, so the state view shows the greater
// Minnesota cities individually and represents the metro as a single cluster
// that opens the metro view. Both maps use the real Minnesota boundary,
// projected equirectangular with a cos(lat) correction, and every pin sits at
// its location's actual coordinates.

const STATE_PATH =
  "M521,452 L513,446 L493,457 L493,543 L487,552 L458,564 L435,595 L434,615 L445,617 L458,635 L446,657 L448,682 L441,735 L467,761 L488,764 L499,780 L529,795 L534,815 L563,839 L578,845 L598,876 L595,899 L600,916 L585,916 L78,916 L78,656 L55,639 L37,611 L65,580 L67,564 L63,506 L51,491 L43,459 L44,420 L40,414 L37,321 L17,272 L10,244 L7,185 L13,165 L0,119 L207,119 L207,64 L227,65 L240,77 L253,152 L264,161 L296,163 L300,170 L338,173 L343,189 L376,185 L376,179 L401,171 L424,174 L449,186 L457,201 L471,200 L485,232 L492,219 L517,212 L521,226 L551,235 L551,248 L565,258 L595,253 L613,238 L638,230 L647,251 L664,246 L684,251 L708,248 L734,266 L760,263 L758,271 L724,289 L678,304 L648,319 L605,356 L586,379 L558,405 L513,440 L521,452 Z";

const METRO_BORDER_PATH =
  "M1305,-1224 L1253,-1265 L1118,-1188 L1118,-628 L1078,-571 L887,-493 L734,-291 L723,-157 L799,-146 L884,-27 L807,118 L821,278 L774,625 L950,796 L1089,812 L1158,916 L1363,1019 L1396,1144 L1586,1304 L1693,1340 L1821,1548 L1802,1698 L1839,1807 L1737,1807 L-1660,1807 L-1660,107 L-1814,-1 L-1931,-183 L-1748,-385 L-1734,-493 L-1759,-872 L-1840,-970 L-1895,-1177 L-1884,-1431 L-1909,-1473 L-1931,-2079 L-2063,-2400 L-2114,-2581 L-2136,-2965 L-2092,-3094 L-2180,-3395 L-793,-3395 L-793,-3757 L-661,-3747 L-573,-3674 L-485,-3182 L-416,-3125 L-196,-3110 L-171,-3063 L86,-3042 L115,-2939 L335,-2965 L335,-3006 L507,-3058 L657,-3037 L829,-2959 L876,-2861 L975,-2871 L1067,-2659 L1111,-2747 L1279,-2788 L1308,-2700 L1506,-2638 L1506,-2555 L1605,-2488 L1806,-2524 L1927,-2617 L2092,-2674 L2150,-2535 L2264,-2566 L2399,-2535 L2557,-2555 L2736,-2436 L2908,-2457 L2893,-2405 L2670,-2286 L2359,-2193 L2158,-2094 L1868,-1851 L1744,-1701 L1554,-1530 L1253,-1302 L1305,-1224 Z";

const METRO_CENTRE = { x: 397, y: 698 };

interface Pin {
  slug: string;
  name: string;
  x: number;
  y: number;
  label?: boolean;
}

// Greater Minnesota. `label` marks the pins with enough clear space to carry a
// permanent name; the rest reveal on hover so the labels never collide.
const STATE_PINS: Pin[] = [
  { slug: "rochester", name: "Rochester", x: 476, y: 840, label: true },
  { slug: "duluth", name: "Duluth", x: 511, y: 441, label: true },
  { slug: "st-cloud", name: "St. Cloud", x: 306, y: 618, label: true },
  { slug: "mankato", name: "Mankato", x: 322, y: 820, label: true },
  { slug: "brainerd", name: "Brainerd", x: 302, y: 502, label: true },
  { slug: "monticello", name: "Monticello", x: 343, y: 655 },
  { slug: "elk-river", name: "Elk River", x: 366, y: 655 },
  { slug: "buffalo", name: "Buffalo", x: 335, y: 674 },
  { slug: "northfield", name: "Northfield", x: 406, y: 777 },
  { slug: "red-wing", name: "Red Wing", x: 469, y: 762, label: true },
  { slug: "faribault", name: "Faribault", x: 395, y: 801 },
  { slug: "owatonna", name: "Owatonna", x: 400, y: 832 },
];

const METRO_PINS: Pin[] = [
  { slug: "andover", name: "Andover", x: 451, y: 168 },
  { slug: "anoka", name: "Anoka", x: 388, y: 202 },
  { slug: "arden-hills", name: "Arden Hills", x: 541, y: 342 },
  { slug: "blaine", name: "Blaine", x: 489, y: 237 },
  { slug: "bloomington", name: "Bloomington", x: 447, y: 540 },
  { slug: "brooklyn-park", name: "Brooklyn Park", x: 408, y: 300 },
  { slug: "chanhassen", name: "Chanhassen", x: 291, y: 519 },
  { slug: "columbia-heights", name: "Columbia Heights", x: 481, y: 351 },
  { slug: "coon-rapids", name: "Coon Rapids", x: 454, y: 276 },
  { slug: "cottage-grove", name: "Cottage Grove", x: 684, y: 552 },
  { slug: "crystal", name: "Crystal", x: 405, y: 359 },
  { slug: "eagan", name: "Eagan", x: 535, y: 574 },
  { slug: "eden-prairie", name: "Eden Prairie", x: 331, y: 527 },
  { slug: "edina", name: "Edina", x: 412, y: 493 },
  { slug: "excelsior", name: "Excelsior", x: 268, y: 481 },
  { slug: "forest-lake", name: "Forest Lake", x: 656, y: 126 },
  { slug: "fridley", name: "Fridley", x: 470, y: 308 },
  { slug: "golden-valley", name: "Golden Valley", x: 406, y: 398 },
  { slug: "hopkins", name: "Hopkins", x: 380, y: 460 },
  { slug: "lake-elmo", name: "Lake Elmo", x: 727, y: 395 },
  { slug: "lauderdale", name: "Lauderdale", x: 509, y: 390 },
  { slug: "little-canada", name: "Little Canada", x: 588, y: 363 },
  { slug: "mahtomedi", name: "Mahtomedi", x: 678, y: 326 },
  { slug: "maple-grove", name: "Maple Grove", x: 341, y: 321 },
  { slug: "minnetonka", name: "Minnetonka", x: 333, y: 464 },
  { slug: "new-brighton", name: "New Brighton", x: 511, y: 327 },
  { slug: "new-hope", name: "New Hope", x: 388, y: 353 },
  { slug: "plymouth", name: "Plymouth", x: 342, y: 379 },
  { slug: "richfield", name: "Richfield", x: 457, y: 500 },
  { slug: "roseville", name: "Roseville", x: 541, y: 383 },
  { slug: "st-anthony", name: "St. Anthony", x: 500, y: 369 },
  { slug: "st-louis-park", name: "St. Louis Park", x: 413, y: 438 },
  { slug: "spring-lake-park", name: "Spring Lake Park", x: 487, y: 287 },
  { slug: "spring-park", name: "Spring Park", x: 224, y: 451 },
  { slug: "st-paul", name: "St. Paul", x: 586, y: 433 },
  { slug: "stillwater", name: "Stillwater", x: 776, y: 336 },
  { slug: "wayzata", name: "Wayzata", x: 307, y: 414 },
  { slug: "white-bear-lake", name: "White Bear Lake", x: 640, y: 310 },
  { slug: "woodbury", name: "Woodbury", x: 673, y: 461 },
];

export const ServiceAreaMap = () => {
  const [view, setView] = useState<"state" | "metro">("state");
  const [active, setActive] = useState<Pin | null>(null);

  const pins = view === "state" ? STATE_PINS : METRO_PINS;
  const viewBox = view === "state" ? "0 0 760 980" : "0 0 1000 700";

  const tooltip = (pin: Pin, scale: number) => (
    <g pointerEvents="none">
      <rect
        x={pin.x - Math.max(52, pin.name.length * 8) * scale}
        y={pin.y - 58 * scale}
        width={Math.max(104, pin.name.length * 16) * scale}
        height={36 * scale}
        rx={7 * scale}
        className="fill-foreground"
      />
      <text
        x={pin.x}
        y={pin.y - 34 * scale}
        textAnchor="middle"
        className="fill-background"
        style={{ fontSize: 20 * scale, fontWeight: 600 }}
      >
        {pin.name}
      </text>
    </g>
  );

  const scale = view === "state" ? 0.85 : 1;

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
        <h3 className="font-heading text-lg font-semibold text-foreground">
          {view === "state" ? "Where we work in Minnesota" : "Twin Cities metro service areas"}
        </h3>
        {view === "metro" && (
          <button
            type="button"
            onClick={() => {
              setView("state");
              setActive(null);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-secondary hover:text-secondary"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to Minnesota
          </button>
        )}
      </div>

      <svg
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        className="h-[420px] w-full sm:h-[560px]"
        role="img"
        aria-label={
          view === "state"
            ? "Map of Minnesota showing Kodiak Structures and Exteriors service areas"
            : "Map of Kodiak Structures and Exteriors service areas across the Twin Cities metro"
        }
      >
        <path
          d={view === "state" ? STATE_PATH : METRO_BORDER_PATH}
          className={view === "state" ? "fill-muted stroke-border" : "fill-background stroke-border"}
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />

        {pins.map((pin) => {
          const isActive = active?.slug === pin.slug;
          return (
            <Link
              key={pin.slug}
              to={`/locations/${pin.slug}`}
              aria-label={`${pin.name} service area`}
              onMouseEnter={() => setActive(pin)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(pin)}
              onBlur={() => setActive(null)}
            >
              <circle
                cx={pin.x}
                cy={pin.y}
                r={(isActive ? 24 : 16) * scale}
                className="fill-secondary/25 transition-all"
              />
              <circle
                cx={pin.x}
                cy={pin.y}
                r={(isActive ? 10 : 7.5) * scale}
                className="fill-secondary stroke-background transition-all"
                strokeWidth={2.5}
              />
              {pin.label && !isActive && (
                <text
                  x={pin.x + 22}
                  y={pin.y + 6}
                  className="fill-foreground"
                  style={{ fontSize: 19, fontWeight: 600 }}
                >
                  {pin.name}
                </text>
              )}
            </Link>
          );
        })}

        {view === "state" && (
          <g
            onClick={() => {
              setView("metro");
              setActive(null);
            }}
            className="cursor-pointer"
          >
            <circle cx={METRO_CENTRE.x} cy={METRO_CENTRE.y} r={30} className="fill-secondary/25" />
            <circle
              cx={METRO_CENTRE.x}
              cy={METRO_CENTRE.y}
              r={21}
              className="fill-secondary stroke-background"
              strokeWidth={3}
            />
            <text
              x={METRO_CENTRE.x}
              y={METRO_CENTRE.y + 7}
              textAnchor="middle"
              className="fill-secondary-foreground"
              style={{ fontSize: 19, fontWeight: 700 }}
            >
              39
            </text>
            <text
              x={METRO_CENTRE.x + 38}
              y={METRO_CENTRE.y + 6}
              className="fill-foreground"
              style={{ fontSize: 19, fontWeight: 600 }}
            >
              Twin Cities metro
            </text>
          </g>
        )}

        {active && tooltip(active, scale)}
      </svg>

      <div className="flex items-start gap-2 border-t border-border px-5 py-3 text-sm text-muted-foreground">
        <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-secondary" aria-hidden="true" />
        <span>
          {view === "state"
            ? "Select the Twin Cities marker to see all 39 metro service areas, or pick a city to open that page."
            : "Hover a pin for the city, or select one to open that service area."}
        </span>
      </div>
    </div>
  );
};
